import { Flex } from 'antd'
import { FunctionComponent } from 'react'
import { useMount } from 'react-use'
import styled from 'styled-components'
import { OurIntlShape } from '@/i18n/typing'
import { designLayoutSize, PptxObjectType } from '@/utils/export-pptx/constants'
import { KolLevelSlide } from '@/utils/export-pptx/types/slide'

interface SlideProps {
  slide: KolLevelSlide
  onSlideReady?: (slide: KolLevelSlide) => void
  formatMessage: OurIntlShape['formatMessage']
}

const SlideComponent: FunctionComponent<SlideProps> = ({
  slide,
  onSlideReady,
  formatMessage,
}) => {
  useMount(() => {
    onSlideReady?.(slide)
  })

  return (
    <Wrapper data-pptx-object={PptxObjectType.Shape}>
      <TitleBlock>
        <Title data-pptx-object={PptxObjectType.Text}>
          {formatMessage({ id: 'general:pptx_title_kol_level' })}
        </Title>
        <TopBrandLine data-pptx-object={PptxObjectType.Shape} />
      </TitleBlock>
      <BottomBrandLine data-pptx-object={PptxObjectType.Shape} />
      <BarsWrapper>
        <Bar1>
          <div data-pptx-object={PptxObjectType.Shape}>
            <span data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:mega_influencer' })}
            </span>
          </div>
          <div>
            <span data-pptx-object={PptxObjectType.Text}>1M+</span>
          </div>
        </Bar1>
        <Bar2>
          <div data-pptx-object={PptxObjectType.Shape}>
            <span data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:large_influencer' })}
            </span>
          </div>
          <div>
            <span data-pptx-object={PptxObjectType.Text}>500K-1M</span>
          </div>
        </Bar2>
        <Bar3>
          <div data-pptx-object={PptxObjectType.Shape}>
            <span data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:medium_large_influencer' })}
            </span>
          </div>
          <div>
            <span data-pptx-object={PptxObjectType.Text}>300K-500K</span>
          </div>
        </Bar3>
        <Bar4>
          <div data-pptx-object={PptxObjectType.Shape}>
            <span data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:medium_influencer' })}
            </span>
          </div>
          <div>
            <span data-pptx-object={PptxObjectType.Text}>100K-300K</span>
          </div>
        </Bar4>
        <Bar5>
          <div data-pptx-object={PptxObjectType.Shape}>
            <span data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:small_medium_influencer' })}
            </span>
          </div>
          <div>
            <span data-pptx-object={PptxObjectType.Text}>50K-100K</span>
          </div>
        </Bar5>
        <Bar6>
          <div data-pptx-object={PptxObjectType.Shape}>
            <span data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:small_influencer' })}
            </span>
          </div>
          <div>
            <span data-pptx-object={PptxObjectType.Text}>30K-50K</span>
          </div>
        </Bar6>
        <Bar7>
          <div data-pptx-object={PptxObjectType.Shape}>
            <span data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:micro_influencer' })}
            </span>
          </div>
          <div>
            <span data-pptx-object={PptxObjectType.Text}>10K-50K</span>
          </div>
        </Bar7>
        <Bar8>
          <div data-pptx-object={PptxObjectType.Shape}>
            <span data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:tiny_influencer' })}
            </span>
          </div>
          <div>
            <span data-pptx-object={PptxObjectType.Text}>{'<10K'}</span>
          </div>
        </Bar8>
      </BarsWrapper>
      <CardList>
        <Card highlight data-pptx-object={PptxObjectType.Shape}>
          <Flex align='center' gap={16}>
            <CardTitle data-pptx-object={PptxObjectType.Text}>
              {formatMessage(
                { id: 'general:pptx_kol_level_title_1' },
                {
                  count:
                    slide.payload.level6 +
                    slide.payload.level7 +
                    slide.payload.level8,
                },
              )}
            </CardTitle>
            <CardFollower data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:pptx_kol_level_follower_1' })}
            </CardFollower>
          </Flex>
          <CardDesc data-pptx-object={PptxObjectType.Text}>
            {formatMessage({ id: 'general:pptx_kol_level_desc_1' })}
          </CardDesc>
        </Card>
        <Card highlight data-pptx-object={PptxObjectType.Shape}>
          <Flex align='center' gap={16}>
            <CardTitle data-pptx-object={PptxObjectType.Text}>
              {formatMessage(
                { id: 'general:pptx_kol_level_title_2' },
                {
                  count: slide.payload.level5,
                },
              )}
            </CardTitle>
            <CardFollower data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:pptx_kol_level_follower_2' })}
            </CardFollower>
          </Flex>
          <CardDesc data-pptx-object={PptxObjectType.Text}>
            {formatMessage({ id: 'general:pptx_kol_level_desc_2' })}
          </CardDesc>
        </Card>
        <Card highlight data-pptx-object={PptxObjectType.Shape}>
          <Flex align='center' gap={16}>
            <CardTitle data-pptx-object={PptxObjectType.Text}>
              {formatMessage(
                { id: 'general:pptx_kol_level_title_3' },
                {
                  count: slide.payload.level4,
                },
              )}
            </CardTitle>
            <CardFollower data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:pptx_kol_level_follower_3' })}
            </CardFollower>
          </Flex>
          <CardDesc data-pptx-object={PptxObjectType.Text}>
            {formatMessage({ id: 'general:pptx_kol_level_desc_3' })}
          </CardDesc>
        </Card>
        <Card highlight data-pptx-object={PptxObjectType.Shape}>
          <Flex align='center' gap={16}>
            <CardTitle data-pptx-object={PptxObjectType.Text}>
              {formatMessage(
                { id: 'general:pptx_kol_level_title_4' },
                {
                  count: slide.payload.level3,
                },
              )}
            </CardTitle>
            <CardFollower data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:pptx_kol_level_follower_4' })}
            </CardFollower>
          </Flex>
          <CardDesc data-pptx-object={PptxObjectType.Text}>
            {formatMessage({ id: 'general:pptx_kol_level_desc_4' })}
          </CardDesc>
        </Card>
      </CardList>
    </Wrapper>
  )
}

const BrandLine = styled.div`
  position: absolute;
  background: ${({ theme }): string => theme.colors.brand.primary};
  height: 1px;
`

const TopBrandLine = styled(BrandLine)`
  position: static;
  top: 72px;
  left: 358px;
  right: 64px;
`

const BottomBrandLine = styled(BrandLine)`
  bottom: 80px;
  left: 64px;
  right: 64px;
`

const Title = styled.div`
  color: ${({ theme }): string => theme.colors.text.primary};
  font-size: 42px;
  font-weight: 700;
  line-height: 70px;
`

const TitleBlock = styled.div`
  position: absolute;
  top: 37px;
  left: 64px;
  right: 64px;
  display: flex;
  gap: 24px;

  ${Title} {
    flex: 0 0 auto;
  }

  ${TopBrandLine} {
    margin-top: 34px;
    flex: 1 1 auto;
  }
`

const Wrapper = styled.div`
  font-family: Arial;
  width: ${designLayoutSize.w}px;
  height: ${designLayoutSize.h}px;
  position: relative;
  /* border: solid 1px ${({ theme }): string => theme.colors.text.primary}; */
  background-color: ${({ theme }): string => theme.colors.border.divider};
  color: ${({ theme }): string => theme.colors.text.primary};

  &,
  * {
    box-sizing: border-box;
  }
`

const Bar = styled.div`
  display: flex;

  div {
    display: flex;
    align-items: center;
    padding: 8px 44px;
    font-size: 24px;
    font-weight: 700;
    line-height: 28px;
    height: 71px;
  }
`

const Bar1 = styled(Bar)`
  div {
    :first-child {
      background: #000;
      width: 200px;
      color: ${({ theme }): string => theme.colors.text.white};
    }
    :last-child {
      color: ${({ theme }): string => theme.colors.text.primary};
    }
  }
`

const Bar2 = styled(Bar)`
  div {
    :first-child {
      background: #222;
      width: 252px;
      color: ${({ theme }): string => theme.colors.text.white};
    }
    :last-child {
      color: ${({ theme }): string => theme.colors.text.primary};
    }
  }
`

const Bar3 = styled(Bar)`
  div {
    :first-child {
      background: #444;
      width: 305px;
      color: ${({ theme }): string => theme.colors.text.white};
    }
    :last-child {
      color: ${({ theme }): string => theme.colors.text.primary};
    }
  }
`

const Bar4 = styled(Bar)`
  div {
    :first-child {
      background: #666;
      width: 359px;
      color: ${({ theme }): string => theme.colors.text.white};
    }
    :last-child {
      color: ${({ theme }): string => theme.colors.text.primary};
    }
  }
`

const Bar5 = styled(Bar)`
  div {
    :first-child {
      background: #888;
      width: 413px;
      color: ${({ theme }): string => theme.colors.text.white};
    }
    :last-child {
      color: ${({ theme }): string => theme.colors.text.primary};
    }
  }
`

const Bar6 = styled(Bar)`
  div {
    :first-child {
      background: #aaa;
      width: 467px;
      color: ${({ theme }): string => theme.colors.text.primary};
    }
    :last-child {
      color: ${({ theme }): string => theme.colors.text.primary};
    }
  }
`

const Bar7 = styled(Bar)`
  div {
    :first-child {
      background: #ccc;
      width: 522px;
      color: ${({ theme }): string => theme.colors.text.primary};
    }
    :last-child {
      color: ${({ theme }): string => theme.colors.text.primary};
    }
  }
`

const Bar8 = styled(Bar)`
  div {
    :first-child {
      background: #fafafa;
      width: 575px;
      color: ${({ theme }): string => theme.colors.text.primary};
    }
    :last-child {
      color: ${({ theme }): string => theme.colors.text.primary};
    }
  }
`

const BarsWrapper = styled.div`
  position: absolute;
  top: 137px;
  left: 64px;
`

const CardTitle = styled.div`
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
  color: ${({ theme }): string => theme.colors.brand.primary};
`

const CardFollower = styled.div`
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  color: ${({ theme }): string => theme.colors.text.primary};
`

const CardDesc = styled.div`
  font-family: 'Noto Sans TC';
  font-size: 18px;
  line-height: 1.2;
  color: ${({ theme }): string => theme.colors.text.secondary};
`

const Card = styled.div<{ highlight: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 10px;
  background: ${({ theme }): string => theme.colors.background.white};
  border: ${({ highlight, theme }): string =>
    highlight ? `solid 4px ${theme.colors.brand.primary}` : 'none'};
  border-radius: 12px;
  padding: 8px 32px;
  width: 540px;
  min-height: 122px;
`

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  position: absolute;
  left: 818px;
  top: 125px;
`

export default SlideComponent
